import type { IntlShape, MessageDescriptor } from 'react-intl';

import { canonicalToolId } from '../toolIdentity';
import { voiceAssistantMessages as m } from './voice-assistant.messages';

/**
 * Friendly one-liners for Talk to Heidi activity rows.
 * Prefer these over raw ``ui.open_artifact`` / humanized-id titles.
 */
const FRIENDLY_TOOL_LABELS: Record<string, MessageDescriptor> = {
  ui_open_artifact: m.activityOpeningOnScreen,
  ui_navigate: m.activityOpeningPage,
  ui_show_toast: m.activityShowingNotice,
  ui_highlight: m.activityHighlighting,
  ui_open_template_picker: m.activityOpeningTemplatePicker,
  help_lookup: m.activityLookingUpHelp,
  help_start_guide: m.activityStartingGuide,
  help_check_status: m.activityCheckingStatus,
  create_session: m.activityCreatingSession,
  create_patient_profile: m.activityCreatingPatient,
  link_session_to_patient: m.activityLinkingPatient,
  get_session: m.activityReadingSession,
  get_consult_note: m.activityReadingNote,
  get_patient_summary: m.activityReadingPatient,
  delegate_to_agent: m.activityWorkingOnIt,
};

const FRIENDLY_UI_ACTION_LABELS: Record<string, MessageDescriptor> = {
  open_artifact: m.activityOpenedOnScreen,
  navigate: m.activityOpenedPage,
  toast: m.activityShowedNotice,
  highlight: m.activityHighlighted,
  open_template_picker: m.activityOpenedTemplatePicker,
  start_guide: m.activityStartedGuide,
};

/** True when ``title`` is just the tool id with spaces (e.g. "Ui open artifact"). */
export const isRawHumanizedToolTitle = (title: string, toolName: string): boolean => {
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return normalize(title) === normalize(toolName);
};

export const formatVoiceToolLabel = (
  intl: IntlShape,
  name: string,
  title?: string
): string => {
  const known = FRIENDLY_TOOL_LABELS[canonicalToolId(name)];
  if (known) return intl.formatMessage(known);
  // Prefer server title; never prettify the registry id (plan L1 retired).
  if (title && !isRawHumanizedToolTitle(title, name)) return title;
  if (title) return title;
  return intl.formatMessage(m.activityWorkingEllipsis);
};

export const formatVoiceUiActionLabel = (
  intl: IntlShape,
  action: string,
  detail?: string
): string => {
  const descriptor = FRIENDLY_UI_ACTION_LABELS[action] ?? m.activityUpdatedScreen;
  const base = intl.formatMessage(descriptor);
  // Keep technical detail out of the clinician-facing row (ok type=session…).
  if (!detail || /^(ok|failed)\b/i.test(detail)) return base;
  return intl.formatMessage(m.activityLabelWithDetail, { base, detail });
};

export const formatVoiceApprovalLabel = (
  intl: IntlShape,
  phase: string,
  summary?: string,
  tool?: string
): string => {
  if (phase === 'request') {
    return summary
      ? intl.formatMessage(m.activityNeedsConfirmationWithSummary, { summary })
      : intl.formatMessage(m.activityNeedsConfirmation);
  }
  if (summary) {
    return intl.formatMessage(m.activityConfirmedWithSummary, { summary });
  }
  if (tool) {
    return intl.formatMessage(m.activityConfirmedWithSummary, {
      summary: formatVoiceToolLabel(intl, tool),
    });
  }
  return intl.formatMessage(m.activityConfirmed);
};
